import { isPlatform } from '@ionic/vue';
import mixpanelInstance, { resetMixpanelState } from '@/utils/mixpanel';
import { clearDatadogRumUser } from '@/utils/datadog';
import { clearOtaChannel } from '@/utils/ota';

const HOMEPAGE_CACHE_KEYS = [
  'hogc_middleNavIcons',
  'hogc_featuredCards',
  'hogc_allAnimations',
  'hogc_unreadCount',
];

/**
 * Clear all analytics service sessions (OneSignal, Mixpanel, Datadog)
 * Should be called on logout or when authentication fails (401)
 *
 * @param {object} [options]
 * @param {boolean} [options.preserveAnalyticsIdentity=false]
 *   Pass true on auth-FAILURE paths (transient 401, expired/missing refresh
 *   token) so the Fix-C cached profile snapshot survives and the next launch
 *   can still identify Mixpanel without waiting on Auth0. The current
 *   session's identity is still reset either way. Explicit user sign-out must
 *   leave this false so a shared device cannot leak identity to the next
 *   user. Cross-user safety on the preserve path is still covered by
 *   validateCachedIdentityAgainstAuth() and the snapshot's 30-day freshness
 *   window. Honored on native only — on web the snapshot is always cleared
 *   because auth0.logout() redirects to origin and skips the native `/logout`
 *   cleanup, so a preserved snapshot would leak identity at the next web boot.
 */
export async function clearAnalyticsSessions({ preserveAnalyticsIdentity = false } = {}) {
  // Stop offering this device live updates until someone signs in again. It
  // belongs here rather than on the Sign Out button because sign-out happens
  // through at least six paths — the 401 interceptor and an expired refresh
  // token among them — and a device that fell out of a session that way would
  // otherwise keep its channel assignment and keep receiving bundles.
  //
  // Bounded, not awaited to completion: this is an HTTP round trip to Capgo
  // whose plugin timeout is 20s, and every sign-out path blocks on this
  // function before redirecting to Auth0. A degraded network must not turn
  // signing out into a twenty-second wait. If it loses the race the assignment
  // survives, and the next sign-in/out clears it.
  await Promise.race([
    clearOtaChannel().catch(() => {}),
    new Promise(resolve => setTimeout(resolve, 2000)),
  ]);

  // Clear homepage caches so the next user gets fresh data
  HOMEPAGE_CACHE_KEYS.forEach(key => {
    try {
      localStorage.removeItem(key);
    } catch (_) {}
  });
  const isNative = isPlatform('android') || isPlatform('ios');
  const isNativeBrowser = isPlatform('mobileweb');
  const isNativeApp = isNative && !isNativeBrowser;

  // Only honor the preserve flag on native. On web, auth0.logout() redirects to
  // window.location.origin (see createLogoutUri), so the native `/logout`
  // deep-link handler — the path that performs the later full cleanup — never
  // runs. A preserved snapshot would then survive into the next web boot, where
  // identifyFromCache() runs before Auth0 resolves and anonymous Page Viewed
  // events get attributed to the previous member (leaking identity on the
  // signed-out landing page or to the next user on a shared browser). Clearing
  // it on web is safe: the cached fast-path only benefits native relaunch.
  const preserveCachedProfile = preserveAnalyticsIdentity && isNativeApp;

  try {
    // Clear OneSignal user session
    if (isNativeApp) {
      try {
        const OneSignal = (await import('onesignal-cordova-plugin')).default;
        if (OneSignal && typeof OneSignal.logout === 'function') {
          OneSignal.logout();
          console.log('[OneSignal] User session cleared');
        }
      } catch (oneSignalError) {
        console.warn('[OneSignal] Error clearing user session:', oneSignalError);
      }
    }

    // Clear Mixpanel user session
    try {
      if (mixpanelInstance && typeof mixpanelInstance.reset === 'function') {
        mixpanelInstance.reset();
        console.log('[Mixpanel] User session cleared');
      }
      // Fix C — also wipe module-level init flags so the next login re-runs
      // init. On native auth-failure paths the cached profile snapshot is
      // preserved so the next launch can identify without a successful Auth0
      // round-trip; on web it is always cleared (see preserveCachedProfile).
      resetMixpanelState({ preserveCachedProfile });
    } catch (mixpanelError) {
      console.warn('[Mixpanel] Error clearing user session:', mixpanelError);
    }

    // Clear Datadog RUM user session
    try {
      clearDatadogRumUser();
      console.log('[Datadog RUM] User session cleared');
    } catch (datadogError) {
      console.warn('[Datadog RUM] Error clearing user session:', datadogError);
    }
  } catch (error) {
    console.error('[clearAnalyticsSessions] Error clearing analytics sessions:', error);
  }
}
